import React from 'react'
import { Button, Card, CardContent, CardHeader, Input, Label, Select, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { REWARDS_EXCHANGES, type RewardsExchange } from '../../types'
import { createRewardsAffiliateUuid, listRewardsAffiliateUuids, setRewardsAffiliateUuidActive } from '../../services/rewardsAffiliateUuidsService'

export default function RewardsSettingsAffiliateUuidsCard() {
  const { add: toast } = useToast()
  const [exchange, setExchange] = React.useState<RewardsExchange>('bybit')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [items, setItems] = React.useState<any[]>([])
  const [newUuid, setNewUuid] = React.useState('')
  const [newActive, setNewActive] = React.useState(true)

  const load = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      setItems(await listRewardsAffiliateUuids(exchange))
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar UUIDs')
    } finally {
      setLoading(false)
    }
  }, [exchange])

  React.useEffect(() => {
    load()
  }, [load])

  async function add() {
    const uuid = newUuid.trim()
    if (!uuid) return
    setSaving(true)
    setError(null)
    try {
      await createRewardsAffiliateUuid({ exchange, affiliateUuid: uuid, active: newActive })
      setNewUuid('')
      toast({ title: 'Rewards - UUID', description: 'UUID cadastrado.', variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao cadastrar UUID')
      toast({ title: 'Rewards - UUID', description: e?.message || 'Falha ao cadastrar UUID', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function makeActive(id: string) {
    setSaving(true)
    setError(null)
    try {
      await setRewardsAffiliateUuidActive({ exchange, id })
      toast({ title: 'Rewards - UUID', description: 'UUID ativado.', variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao ativar')
      toast({ title: 'Rewards - UUID', description: e?.message || 'Falha ao ativar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - Affiliate UUID (MASTER)</CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-foreground/70">
          Nenhum UUID é hardcoded. O fluxo do Rewards depende do UUID ativo por corretora.
        </p>

        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Corretora</Label>
            <Select value={exchange} onChange={(e) => setExchange((e.target as HTMLSelectElement).value as RewardsExchange)} disabled={saving}>
              {REWARDS_EXCHANGES.map((x) => (
                <option key={x} value={x}>
                  {x.toUpperCase()}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Novo UUID</Label>
            <Input value={newUuid} onChange={(e) => setNewUuid((e.target as HTMLInputElement).value)} placeholder="UUID do afiliado..." disabled={saving} />
            <div className="flex items-center gap-2 pt-1">
              <Label className="text-xs">Ativar ao criar</Label>
              <input type="checkbox" checked={newActive} onChange={(e) => setNewActive((e.target as HTMLInputElement).checked)} disabled={saving} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={add} disabled={saving || !newUuid.trim()}>
            {saving ? 'Salvando...' : 'Adicionar UUID'}
          </Button>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">UUIDs cadastrados ({exchange.toUpperCase()})</div>
          {loading ? (
            <div className="text-sm text-foreground/70">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">
              Nenhum UUID cadastrado ainda.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{it.affiliate_uuid}</div>
                    <div className="text-xs text-foreground/60">Criado em: {new Date(it.created_at).toLocaleString('pt-BR')}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {it.active ? (
                      <Badge variant="soft" color="success">
                        Ativo
                      </Badge>
                    ) : (
                      <Badge variant="soft" color="secondary">
                        Inativo
                      </Badge>
                    )}
                    {!it.active && (
                      <Button variant="outline" onClick={() => makeActive(it.id)} disabled={saving}>
                        Tornar ativo
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

